import assert from 'assert';

import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';

/** Generic helper function to get the published flowchart version from a procedure. This supports both the
 * legacy and proxy versions, and favors the proxy version when it is available.
 *
 * @param procedure - The procedure to get the published flowchart version from.
 * @returns The published flowchart version.
 */
export function getPowAgnosticPublishedFlowchartVersionFromProcedure<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureNode';
    procedureFlowchart: {
      __typename: 'ProcedureFlowchartNode';
      publishedFlowchartVersion?: TLegacyVersion;
    };
    publishedProcedureFlowchartConfigVersionProxy?: TPowVersion;
  },
>(
  procedure: T
): T['publishedProcedureFlowchartConfigVersionProxy'] | T['procedureFlowchart']['publishedFlowchartVersion'] {
  return (
    procedure.publishedProcedureFlowchartConfigVersionProxy ??
    procedure.procedureFlowchart.publishedFlowchartVersion
  );
}

/**
 * Extracts the published procedure flowchart version ID from a procedure, or
 * the latest flowchart version if publishing is disabled.  Returns null if no
 * published flowchart version is available or if it is inaccessible.
 *
 * @param procedure - The procedure to extract the published flowchart version ID from.
 * @returns The ID of the published or latest flowchart version depending on
 * publishing status, or null if not available.
 */
export function getPowAgnosticPublishedFlowchartVersionId<
  TLegacyVersion extends {id: string; __typename: 'ProcedureFlowchartVersionNode'},
  TPowVersion extends {id: string; __typename: 'ProcedureFlowchartConfigVersionProxyNode'},
  T extends {
    __typename: 'ProcedureNode';
    procedureFlowchart: {
      __typename: 'ProcedureFlowchartNode';
      publishedFlowchartVersion?: TLegacyVersion | null;
    };
    publishedProcedureFlowchartConfigVersionProxy?: TPowVersion | null;
  },
>(procedure: T): string | null {
  const publishedFlowchartVersion = getPowAgnosticPublishedFlowchartVersionFromProcedure(procedure);
  if (publishedFlowchartVersion == null) {
    return null;
  }
  return getAccessibleOrInaccessibleId(publishedFlowchartVersion);
}

/** Generic helper function to get the latest flowchart version from a procedure. This supports both the
 * legacy and proxy versions, and favors the proxy version when it is available.
 *
 * @param procedure - The procedure to get the latest flowchart version from.
 * @returns The latest flowchart version.
 */
export function getPowAgnosticLatestFlowchartVersionFromProcedure<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureNode';
    procedureFlowchart: {
      __typename: 'ProcedureFlowchartNode';
      latestFlowchartVersion: TLegacyVersion;
    };
    latestProcedureFlowchartConfigVersionProxy?: TPowVersion;
  },
>(
  procedure: T
): T['latestProcedureFlowchartConfigVersionProxy'] | T['procedureFlowchart']['latestFlowchartVersion'] {
  return (
    procedure.latestProcedureFlowchartConfigVersionProxy ??
    procedure.procedureFlowchart.latestFlowchartVersion
  );
}

/** Generic helper function to get the template flowchart version from a procedure run. This supports both the
 * legacy(ProcedureFlowchartVersionNode) and proxy(ProcedureFlowchartConfigVersionProxyNode) versions, and
 * favors the proxy version when it is available.
 *
 * In this function, there are 3 potential return types: ProcedureFlowchartConfigVersionProxyNode,
 * ProcedureFlowchartVersionNode, and InaccessibleInfoNode. While it is possible for procedureFlowchartConfigVersionProxy
 * to be null, it is not possible for templateProcedureFlowchartVersion to be null. Thus, this function will
 * never return null. In terms of the NonNullable type generic, we are defining the return type to be
 * NonNullable<T['procedureFlowchartConfigVersionProxy'] | T['templateProcedureFlowchartVersion']>, because
 * typescript is having a hard time narrowing the return type correctly when TPowVersion and TLegacyVersion are both
 * made of union of types.
 *
 * Why is doing this safe? This is safe, because we can guarantee that we will never have null as
 * templateProcedureFlowchartVersion is not nullable. Also, we are doing a casting to non-nullable while
 * still maintaining the rest of the type received for T['procedureFlowchartConfigVersionProxy'] and
 * T['templateProcedureFlowchartVersion'].
 *
 * @param procedureRun - The procedure run to get the template flowchart version from.
 * @returns The template flowchart version (proxy if available, else legacy). Never returns null.
 */
export function getPowAgnosticTemplateFlowchartVersionFromProcedureRun<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureRunNode';
    templateProcedureFlowchartVersion: TLegacyVersion;
    procedureFlowchartConfigVersionProxy?: TPowVersion | null;
  },
>(
  procedureRun: T
): NonNullable<T['procedureFlowchartConfigVersionProxy'] | T['templateProcedureFlowchartVersion']> {
  return (procedureRun.procedureFlowchartConfigVersionProxy ??
    procedureRun.templateProcedureFlowchartVersion) as NonNullable<
    T['procedureFlowchartConfigVersionProxy'] | T['templateProcedureFlowchartVersion']
  >;
}

/** Generic helper function to get the template flowchart version from a procedure run plan. This supports both the
 * legacy(ProcedureFlowchartVersionNode) and proxy(ProcedureFlowchartConfigVersionProxyNode) versions, and
 * favors the proxy version when it is available.
 *
 * In this function, there are 3 potential return types: ProcedureFlowchartConfigVersionProxyNode,
 * ProcedureFlowchartVersionNode, and InaccessibleInfoNode. While it is possible for procedureFlowchartConfigVersionProxy
 * to be null, it is not possible for templateProcedureFlowchartVersion to be null. Thus, this function will
 * never return null. In terms of the NonNullable type generic, we are defining the return type to be
 * NonNullable<T['procedureFlowchartConfigVersionProxy'] | T['templateProcedureFlowchartVersion']>, because
 * typescript is having a hard time narrowing the return type correctly when TPowVersion and TLegacyVersion are both
 * made of union of types.
 *
 * Why is doing this safe? This is safe, because we can guarantee that we will never have null as
 * templateProcedureFlowchartVersion is not nullable. Also, we are doing a casting to non-nullable while
 * still maintaining the rest of the type received for T['procedureFlowchartConfigVersionProxy'] and
 * T['templateProcedureFlowchartVersion'].
 *
 * @param procedureRunPlan - The procedure run plan to get the template flowchart version from.
 * @returns The template flowchart version (proxy if available, else legacy). Never returns null.
 */
export function getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureRunPlanNode';
    templateProcedureFlowchartVersion: TLegacyVersion;
    procedureFlowchartConfigVersionProxy?: TPowVersion | null;
  },
>(
  procedureRunPlan: T
): NonNullable<T['procedureFlowchartConfigVersionProxy'] | T['templateProcedureFlowchartVersion']> {
  return (procedureRunPlan.procedureFlowchartConfigVersionProxy ??
    procedureRunPlan.templateProcedureFlowchartVersion) as NonNullable<
    T['procedureFlowchartConfigVersionProxy'] | T['templateProcedureFlowchartVersion']
  >;
}

/** Extracts the procedure from a flowchart config version proxy or a procedure flowchart version node.
 *
 * Important: When using this function with a ProcedureFlowchartVersionNode, ensure you are passing a flowchart
 * version that is parented by a procedure, not a procedure run. If you are working with a procedure run,
 * to obtain the procedure, you should use the templateProcedureFlowchartVersion instead of the
 * currentProcedureFlowchartVersion.
 *
 * Why are we asserting that the procedure is non-null? Why is this safe? This is because each procedure flowchart,
 * should always have a parent defined. However, the procedure flowchart graphql resolver returns null when
 * the flowchart parent is a ProcedureRun, which is not ideal. Alternatively, this could be fixed by updating
 * the graphql resolver to return the parent as a ProcedureRun instead of null, but procedures team determined
 * that this is not worth the effort to update as soon it will be deprecated. Asserting non-null is safe,
 * because you are expected to pass only flowchart versions that is expected to be parented by a procedure, which
 * in that case the parent will always be non-null (e.g. templateProcedureFlowchartVersion).
 *
 * This function expects the parent property to be of type ProcedureNode already so typescript linting should
 * catch any cases where you trying to pass in the incorrect flowchart version. Additionally, this function
 * throws an assertion error if the __typename of the parent is not ProcedureNode.
 *
 * @param flowchartVersion - Either a `ProcedureFlowchartConfigVersionProxyNode` (PoW) or a
 *   `ProcedureFlowchartVersionNode` (legacy). Should be a template version, not a plan version.
 * @returns The parent `ProcedureNode` that owns this flowchart version.
 */
export function getPowAgnosticProcedureFromFlowchartVersion<TProcedure extends {__typename: 'ProcedureNode'}>(
  flowchartVersion:
    | {
        __typename: 'ProcedureFlowchartConfigVersionProxyNode';
        procedure: TProcedure;
      }
    | {
        __typename: 'ProcedureFlowchartVersionNode';
        procedureFlowchart: {__typename: 'ProcedureFlowchartNode'; parent: TProcedure | null};
      }
): TProcedure {
  if (flowchartVersion.__typename === 'ProcedureFlowchartConfigVersionProxyNode') {
    return flowchartVersion.procedure;
  }
  const parent = flowchartVersion.procedureFlowchart.parent;
  assert(parent != null, 'Expected the procedure flowchart parent to be set');
  assert(
    parent.__typename === 'ProcedureNode',
    'Expected the procedure flowchart parent to be of type ProcedureNode'
  );
  return parent;
}

/** Generic helper function to get the executing flowchart version from a procedure run.
 *
 * This supports both the flowchart config version proxy and the current flowchart version, and favors the
 * flowchart config version proxy when it is available. Falls back to current flowchart version if flowchart config
 * version proxy is null.
 *
 * The return type here is T['procedureFlowchartConfigVersionProxy'] | T['currentProcedureFlowchartVersion']
 * because we want users to have control over how to handle null, inaccessible or accessible flowchart versions.
 *
 * TLegacyVersion and TPowVersion do not extend their respective types because Typescript does not do a
 * good job at inferring the correct types when we are returning union of types.
 *
 * @param procedureRun - The procedure run to get the executing flowchart version from.
 * @returns The executing flowchart version (flowchart config version proxy if available, else current flowchart version), or null if both are null.
 */
export function getPowAgnosticExecutingFlowchartVersionFromProcedureRun<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureRunNode';
    currentProcedureFlowchartVersion?: TLegacyVersion;
    procedureFlowchartConfigVersionProxy?: TPowVersion;
  },
>(procedureRun: T): T['procedureFlowchartConfigVersionProxy'] | T['currentProcedureFlowchartVersion'] {
  return procedureRun.procedureFlowchartConfigVersionProxy ?? procedureRun.currentProcedureFlowchartVersion;
}
